// Add-stop form -- extracted from LogisticsRoutesDetailCard.tsx as the
// file-size-hygiene preamble for D3e UI (state-action picker on the
// detail card). Same shape it had inline: stop_kind picker with
// stop_kind-conditional recipient fields (mirrors the DB
// logi_stops_recipient_shape_chk via the predicates exported
// from useLogisticsRouteDetail) + structured address (4 fields packed
// into the JSONB column) + optional scheduled window + notes.
//
// Address validation primitive wired in: the AddressValidationAffordance
// sits directly under the 4 address inputs. Click Validate to normalize
// + sanity-check; Apply suggestion writes back via the parent's
// setNewAddress* setters. Advisory only -- never blocks submission.
import { AddressValidationAffordance } from "@/components/common/AddressValidationAffordance";
import {
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "./logisticsRoutesShared";
import {
  stopKindAllowsContactRecipient,
  stopKindAllowsOrgRecipient,
  stopKindRequiresContactRecipient,
  stopKindRequiresOrgRecipient,
  useLogisticsRouteDetail,
} from "./useLogisticsRouteDetail";
import type { LogiStopKind } from "@/lib/logistics";

const STOP_KIND_LABELS: Record<LogiStopKind, string> = {
  plant_origin: "Plant origin",
  dealer_holding: "Dealer holding",
  end_customer: "End customer",
  service: "Service",
  other: "Other",
};

type Props = {
  d: ReturnType<typeof useLogisticsRouteDetail>;
};

export function LogisticsRoutesAddStopForm({ d }: Props) {
  if (!d.adding) return null;
  return (
    <div className="mt-4 rounded-xl border border-gray-200 dark:border-zinc-700 p-3 space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500">
        New stop
      </div>
      {d.addError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {d.addError}
        </div>
      ) : null}
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Stop kind
        <select
          className={`${inputClass} mt-1`}
          value={d.newStopKind}
          onChange={(e) => d.setNewStopKind(e.target.value as LogiStopKind)}
        >
          {d.stopKindOptions.map((k) => (
            <option key={k} value={k}>
              {STOP_KIND_LABELS[k] ?? k}
            </option>
          ))}
        </select>
      </label>
      {stopKindAllowsOrgRecipient(d.newStopKind) ? (
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Recipient dealer org (UUID)
          {stopKindRequiresOrgRecipient(d.newStopKind) ? " — required" : " — optional"}
          <input
            className={`${inputClass} mt-1`}
            value={d.newRecipientOrgId}
            onChange={(e) => d.setNewRecipientOrgId(e.target.value)}
            placeholder="organizations.id of the dealer"
          />
        </label>
      ) : null}
      {stopKindAllowsContactRecipient(d.newStopKind) ? (
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Recipient contact (UUID)
          {stopKindRequiresContactRecipient(d.newStopKind) ? " — required" : " — optional"}
          <input
            className={`${inputClass} mt-1`}
            value={d.newRecipientContactId}
            onChange={(e) => d.setNewRecipientContactId(e.target.value)}
            placeholder="contact uuid"
          />
        </label>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Street
          <input
            className={`${inputClass} mt-1`}
            value={d.newAddressStreet}
            onChange={(e) => d.setNewAddressStreet(e.target.value)}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          City
          <input
            className={`${inputClass} mt-1`}
            value={d.newAddressCity}
            onChange={(e) => d.setNewAddressCity(e.target.value)}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          State
          <input
            className={`${inputClass} mt-1`}
            value={d.newAddressState}
            onChange={(e) => d.setNewAddressState(e.target.value)}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Postal code
          <input
            className={`${inputClass} mt-1`}
            value={d.newAddressPostalCode}
            onChange={(e) => d.setNewAddressPostalCode(e.target.value)}
          />
        </label>
      </div>
      <AddressValidationAffordance
        value={{
          street: d.newAddressStreet,
          city: d.newAddressCity,
          state: d.newAddressState,
          postal_code: d.newAddressPostalCode,
        }}
        onApply={(n) => {
          if (n.street !== undefined) d.setNewAddressStreet(n.street);
          if (n.city !== undefined) d.setNewAddressCity(n.city);
          if (n.state !== undefined) d.setNewAddressState(n.state);
          if (n.postal_code !== undefined) d.setNewAddressPostalCode(n.postal_code);
        }}
      />
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Scheduled window start (optional ISO timestamp)
        <input
          className={`${inputClass} mt-1`}
          value={d.newScheduledStart}
          onChange={(e) => d.setNewScheduledStart(e.target.value)}
          placeholder="2026-06-15T08:00:00Z"
        />
      </label>
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Scheduled window end (optional ISO timestamp; must be ≥ start)
        <input
          className={`${inputClass} mt-1`}
          value={d.newScheduledEnd}
          onChange={(e) => d.setNewScheduledEnd(e.target.value)}
          placeholder="2026-06-15T12:00:00Z"
        />
      </label>
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Notes
        <input
          className={`${inputClass} mt-1`}
          value={d.newNotes}
          onChange={(e) => d.setNewNotes(e.target.value)}
        />
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          className={primaryBtnClass}
          onClick={() => void d.submitAdd()}
          disabled={d.addBusy}
        >
          {d.addBusy ? "Saving…" : "Add stop"}
        </button>
        <button type="button" className={ghostBtnClass} onClick={d.cancelAdd}>
          Cancel
        </button>
      </div>
    </div>
  );
}
